// ============================================================================
// Renders a list of events
// ============================================================================
import { getRichTextHtml, IDictionary } from '@msdyn365-commerce/core';
import moment from 'moment-timezone';
import * as React from 'react';
import { IProductAttributeProps } from './product-lightbox';
import { IPrice, IProductPlacementDataItem } from './product-placement-data-helper';

// ============================================================================
// INTERFACES AND CONSTANTS
// ============================================================================

export interface IEventListRenderProps {
    events: IProductPlacementDataItem[];
    maximum?: number;
    noFutureEventsJSX: JSX.Element;
    timezone: string;
    soldOutText?: string;
    debugMode?: boolean;
}

type maybeMoment = moment.Moment | undefined;

// Internal interface for info on a single event
interface IFormattedEvent {
    id?: number;
    name?: string;
    description?: string;
    price?: IPrice;
    pdp?: string;
    category?: string;
    intro?: string;
    startDate?: maybeMoment;
    endDate?: maybeMoment;
    time?: string;
    location?: string;
    priceText?: string;
    purchaseUrl?: string;
    eventUrlTitle?: string;
    soldOut?: boolean;
    downloadPDFUrl?: string;
    productImage?: string;
}

// -------------------------------------------------------------
// Hides errors by attempting to prevent ambigious values
// -------------------------------------------------------------
function eventSortKey(event: IFormattedEvent): string {
    const date: string = event.startDate!.format('X');

    return `${date}-${event.time}-${event.name}`;
}

// -------------------------------------------------------------
// OLD, ambiguous method. Produces errors in some cases.
// -------------------------------------------------------------
function oldSortKey(event: IFormattedEvent): number {
    return +event.startDate!.format('X');
}

// =============================================================================
// CLASS DEFINITION
// =============================================================================
/**
 * Event list render component
 */
export default class RenderEventList extends React.PureComponent<IEventListRenderProps> {

    // ============================================================================
    // PUBLIC METHODS
    // ============================================================================

    // -------------------------------------------------------------
    // -------------------------------------------------------------
    public render(): JSX.Element | null {
        return this._renderEventList();
    }

    // ============================================================================
    // JSX FRAGMENTS
    // ============================================================================

    // -------------------------------------------------------------
    // Render the event list
    // -------------------------------------------------------------
    private _renderEventList(): JSX.Element {
        const maxToDisplay = this.props.maximum || 999; // Default to a large number, but not ludicrously large

        // Format the event list
        let eventList: IFormattedEvent[] = this._formatList(this.props.events);

        // Clean up the list before display
        eventList = this._sortAndTrimList(eventList, maxToDisplay);

        // If the event list still has something to display after being trimmed
        // then show the list or display the no current events jsx message
        if (eventList.length === 0) {
            return this.props.noFutureEventsJSX;
        } else {
            return (
                <div className='event-list'>
                    {eventList.map((event: IFormattedEvent) => (
                            <div key={event.id} className='event-list-event'>
                                {this._renderEvent(event)}
                            </div>
                        ))}
                </div>
            );
        }
    }

    // -------------------------------------------------------------
    // Render a single event
    // -------------------------------------------------------------
    // tslint:disable: react-no-dangerous-html
    private _renderEvent(event: IFormattedEvent): JSX.Element | null {
        return (
            <React.Fragment>
                <div className='event-list-image'>
                    <a className='event-list-image-link' href={event.pdp}><img src={event.productImage} alt={`${event.name} image`} /></a>
                </div>
                <div className='event-list-details'>
                    {this._renderDateRange(event.startDate, event.endDate)}
                    <div className='event-list-category'>{event.category}</div>
                    <a className='event-list-name' href={event.pdp}>{event.name}</a>
                    <div className='event-list-intro' dangerouslySetInnerHTML={getRichTextHtml(event.intro)} />
                    <div className='event-list-location'>{event.location}</div>
                    <div className='event-list-time'>{event.time}</div>
                    <div className='event-list-price'>{event.priceText}</div>
                    <div className='event-list-button'>
                        <a className='event-list-button-link' href={event.pdp}>Details</a>
                    </div>
                    {/* <div className='event-list-purchase-url'>
                        <a href={event.purchaseUrl}>{event.eventUrlTitle}</a>
                    </div>
                    <div className='event-list-pdf'>
                        <a href={event.downloadPDFUrl}>Download PDF</a>
                    </div> */}
                    {event.soldOut && <div className='event-list-sold-out'>{this.props.soldOutText}</div>}
                </div>
            </React.Fragment>
        );
    }

    // -------------------------------------------------------------
    // Render a single event
    // -------------------------------------------------------------
    private _renderDateRange(startDate: moment.Moment | undefined, endDate: moment.Moment | undefined): JSX.Element | null {

        // Early out if there's no dates
        if (!startDate && !endDate) {
            return null;
        }

        // Both: Display as range
        const isRange = (startDate && endDate);

        return (
            <div className='event-list-date'>

                {/* Single Date */}
                {!isRange &&
                    <div className='event-list-single'>
                        {this._renderDate(startDate!)}
                    </div>
                }

                {/* Date Range */}
                {isRange &&
                    <div className='event-list-range'>
                        <span className='event-list-start'>{this._renderDate(startDate!)}</span>
                        <span className='event-list-separator' /> {/* No content: fill in with css */}
                        <span className='event-list-end'>{this._renderDate(endDate!)}</span>
                    </div>
                }
            </div>
        );
    }

    // -------------------------------------------------------------
    // -------------------------------------------------------------
    private _renderDate(date: moment.Moment): JSX.Element {
        return (
            <React.Fragment>
                {this._renderMonth(date)}
                {this._renderDay(date)}
                {this._renderYear(date)}
            </React.Fragment>
        );
    }

    // -------------------------------------------------------------
    // Render a month in several different formats.
    // It's up to the css to hide unwanted versions.
    // -------------------------------------------------------------
    private _renderMonth(date: moment.Moment): JSX.Element {
        return (
            <React.Fragment>
                <span className='event-list-month-S'>{date.format('M')}</span>
                <span className='event-list-month-M'>{date.format('MMM')}</span>
                <span className='event-list-month-L'>{date.format('MMMM')}</span>
            </React.Fragment>
        );
    }

    // -------------------------------------------------------------
    // Render a year in several different formats.
    // It's up to the css to hide unwanted versions.
    // -------------------------------------------------------------
    private _renderYear(date: moment.Moment): JSX.Element {
        return (
            <React.Fragment>
                <span className='event-list-year-S'>{date.format('YY')}</span>
                <span className='event-list-year-L'>{date.format('YYYY')}</span>
            </React.Fragment>
        );
    }

    // -------------------------------------------------------------
    // Render a day in several different formats (Currently only one).
    // It's up to the css to hide unwanted versions.
    // -------------------------------------------------------------
    private _renderDay(date: moment.Moment): JSX.Element {
        return (
            <React.Fragment>
                <span className='event-list-day'>{date.format('D')}</span>
            </React.Fragment>
        );
    }

    // ============================================================================
    // PRIVATE METHODS
    // ============================================================================

    // -------------------------------------------------------------
    // Formats the product and attribute data into a more useful format
    // -------------------------------------------------------------
    private _formatList(list: IProductPlacementDataItem[]): IFormattedEvent[] {

        const fieldSources: IDictionary<string> = {
            'End Date': 'DateTimeOffsetValue',
            'Start Date': 'DateTimeOffsetValue',
            'Can add to cart': 'BooleanValue',          // Boolean DataTypeValue: 6
            'Sold Out': 'BooleanValue',

            default: 'TextValue'
        };

        return list.map((entry) => {
            // @ts-ignore
            const attributes = this._getAttributes(entry.attributes, fieldSources);

            if (this.props.debugMode) {
                console.log('Event Attributes', attributes);
            }

            const startDate = attributes['Start Date'] ? moment.tz(attributes['Start Date'], this.props.timezone) : undefined;
            const endDate = attributes['End Date'] ? moment.tz(attributes['End Date'], this.props.timezone) : undefined;
            const dates = this._dateLogic(startDate, endDate);
            return {
                id: entry.recordId,
                name: entry.heading,
                price: entry.price,
                pdp: entry.productUrl,
                productImage: entry.image,

                category: attributes['SLR Event Type'],
                intro: attributes.Introduction,
                description: attributes.Body,         // PDP only
                startDate: dates.start,
                endDate: dates.end,
                time: attributes['Time Range'],
                location: attributes['SLR Event Location'],
                priceText: attributes['Event Price Info'],

                purchaseUrl: attributes['Event Purchase URL'],
                eventUrlTitle: attributes['Event URL Title'],
                downloadPDFUrl: attributes['Download PDF URL'],

                soldOut: attributes['Sold Out']
            };
        });

        /* OTHER ATTRIBUTES
            Event purchase
            Event Purchase URL
            Event URL Title
            Featured Y/N
            Video Caption
            Video Title
        */
    }

    // -------------------------------------------------------------
    // Converts the attribute list into a simple hash
    // -------------------------------------------------------------
    // tslint:disable-next-line:no-any
    private _getAttributes(attributes: IProductAttributeProps[], fieldSources: IDictionary<string>): IDictionary<any /* object has root types of string and boolean, makes it painful to redeclare */> {

        return attributes.reduce((output, attribute) => {
            if (attribute.Name) {
                const attributeSource = fieldSources[attribute.Name] || fieldSources.default;
                (output[attribute.Name] = attribute[attributeSource]);
            }

            return output;
        },                       {});       // <--- I hate the `align` rule
    }

    // -------------------------------------------------------------
    // Date logic:
    //   Only start date: Good
    //   Only end date: Move to start date
    //   Start and end are the same: Remove end date
    //   Both: Display as range
    // -------------------------------------------------------------
    private _dateLogic(startDate: moment.Moment | undefined, endDate: moment.Moment | undefined): { start: maybeMoment; end: maybeMoment } {
        // Only end date: Move to start date
        if (endDate && !startDate) {
            startDate = endDate;
            endDate = undefined;
        }

        // Start and end are the same: Remove end date
        if (startDate && startDate.isSame(endDate, 'day')) {
            endDate = undefined;
        }

        return {
            start: startDate,
            end: endDate
        };
    }

    // -------------------------------------------------------------
    // Sort the list by date, then trim it.
    //
    // @FIXME/dg: These should ideally be handled by the server query
    //            to minimize request size
    // -------------------------------------------------------------
    private _sortAndTrimList(eventList: IFormattedEvent[], maxToDisplay: number): IFormattedEvent[] {

        // subtracting 1 from the current date so that we are able to show events
        // on the day of; since we are using the isBefore method.
        const today = moment.tz(Date.now(), this.props.timezone)
            .startOf('day');

        // Filter out events with no dates (logic has already occurred)
        eventList = eventList.filter((event: IFormattedEvent) => {

            // ensure events have a starting date at least, end date is unreliable
            if (event.startDate) {

                // Remove events that have past
                return !this._isEventPast(event, today);
            }

            return false;
        });

        // Sort the list by date
        // @FIXME/dg: Band-aid version. Messy! Move to its own function if we keep this.
        if (eventList.length) {
            eventList = eventList.sort((event1, event2) => {
                const a = this.props.debugMode ? oldSortKey(event1) : eventSortKey(event1);
                const b = this.props.debugMode ? oldSortKey(event2) : eventSortKey(event2);

                if (a < b) {
                    return -1;
                } else if (a > b) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }

        // Trim unwanted events
        if (eventList.length &&
            maxToDisplay &&
            (eventList.length > maxToDisplay)
        ) {
            eventList.splice(maxToDisplay);
        }

        return eventList;
    }

    // -------------------------------------------------------------
    // Determine if an event is passed the day given
    // -------------------------------------------------------------
    private _isEventPast(event: IFormattedEvent, date: moment.Moment): boolean {
        const { startDate, endDate } = event;

        // we want to use the date the references the events closure the closest
        const furthestDate = endDate || startDate!;
        return furthestDate.isBefore(date, 'day');
    }
}
